import { useEffect, useState, type JSX } from 'react';

interface PlanetNakshatraChange {
  nakshatra: string;
  timestamp: string;
}

interface PlanetNakshatraGocharResponse {
  planetName: string;
  nakshtra: PlanetNakshatraChange[];
}

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const PAGE_SIZE = 10;

export default function PlanetNakshatraTransitTable({
  data,
  selectedPlanet,
  setSelectedPlanet,
}: {
  data: PlanetNakshatraGocharResponse[];
  selectedPlanet: string;
  setSelectedPlanet: (v: string) => void;
}): JSX.Element {
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [selectedPlanet]);

  const active = data.find((p) => p.planetName === selectedPlanet) ?? data[0];

  if (!active) {
    return (
      <section className="border border-[#2DAAFD] rounded-xl p-6 mt-8">
        <div className="text-center">No data available.</div>
      </section>
    );
  }

  const totalItems = active.nakshtra.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const paginatedData = active.nakshtra.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section className="border border-[#2DAAFD] rounded-xl p-6 max-w-[1200px] mx-auto mt-8">
      <h2 className="font-32 font-semibold font-spectral mb-8 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
        Planet Nakshatra Transit (Nakshatra Parivartan)
      </h2>

      <div className="flex flex-col md:flex-row gap-6">
        <aside className="md:w-[260px] w-full">
          <div className="border border-[#2DAAFD] rounded-xl p-4 space-y-2">
            {data.map((planet) => (
              <button
                key={planet.planetName}
                onClick={() => setSelectedPlanet(planet.planetName)}
                className={`w-full px-4 py-2 rounded-[10px] font-spectral font-medium text-lg transition-all ${
                  planet.planetName === selectedPlanet
                    ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
                    : 'inner-shadow-blue'
                }`}
              >
                {planet.planetName}
              </button>
            ))}
          </div>
        </aside>

        <main className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginatedData.map((item, index) => (
              <div key={index} className="rounded-xl p-4 relative inner-shadow-blue">
                <div className="absolute font-lato -top-3 -left-3 bg-[linear-gradient(to_right,#082261,#34065c)] text-white w-7 h-7 rounded-full flex items-center justify-center">
                  {page * PAGE_SIZE + index + 1}
                </div>

                <h4 className="font-20 font-lato font-medium bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c]">
                  {item.nakshatra}
                </h4>

                <p className="font-16 font-lato mt-1">{formatDateTime(item.timestamp)}</p>
              </div>
            ))}
          </div>

          {totalItems > PAGE_SIZE && (
            <div className="flex justify-center gap-4 mt-6">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-5 py-2 rounded-lg font-medium border border-[#2DAAFD] disabled:opacity-50"
              >
                Prev
              </button>

              <button
                disabled={page === totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="px-5 py-2 rounded-lg font-medium border border-[#2DAAFD] disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </main>
      </div>
    </section>
  );
}
